import { useTranslation } from "react-i18next";

/**
 * Which providers the diary shows. `options` is the API's
 * `meta.provider_options` — the branch's providers this user may see (a
 * provider sees only themselves) — so nothing here decides reach.
 *
 * Empty `selected` means every provider, which is the default and what
 * "All providers" switches back to. The API filters bookings, blocks, columns
 * and the status counts on whatever is chosen.
 */
export default function DiaryProviderFilter({ options, selected, onChange }) {
  const { t } = useTranslation();

  if (!options?.length) return null;

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((p) => p !== id) : [...selected, id];
    // Everybody ticked is the same as nobody: keep the URL short.
    onChange(next.length === options.length ? [] : next);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-caption font-medium text-ink-2">{t("diary.providerFilter")}</span>

      <div
        role="group"
        aria-label={t("diary.providerFilter")}
        className="-mx-1 flex max-w-full gap-1.5 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0"
      >
        <button
          type="button"
          onClick={() => onChange([])}
          aria-pressed={selected.length === 0}
          className={`h-8 flex-shrink-0 rounded-full border px-3 text-caption font-medium transition-colors ${
            selected.length === 0
              ? "border-accent bg-accent-wash text-accent-text"
              : "border-line bg-surface text-ink-2 hover:border-line-strong"
          }`}
        >
          {t("diary.allProviders")}
        </button>

        {options.map((provider) => {
          const on = selected.includes(provider.id);

          return (
            <button
              key={provider.id}
              type="button"
              onClick={() => toggle(provider.id)}
              aria-pressed={on}
              className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-full border px-3 text-caption font-medium transition-colors ${
                on
                  ? "border-accent bg-accent-wash text-accent-text"
                  : "border-line bg-surface text-ink-2 hover:border-line-strong"
              }`}
            >
              {provider.photo_url ? (
                <img src={provider.photo_url} alt="" className="h-5 w-5 rounded-full object-cover" />
              ) : null}
              {provider.display_name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
